import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getCollaborativeHarmonyDetail } from '../../client';
import { useTranslation } from 'react-i18next';

const BAND_TEXT = {
  low: '낮음',
  mid: '보통',
  high: '높음',
  extreme: '매우 높음',
};

const getBand = (value) => {
  if (value <= 25) return 'low';
  if (value <= 40) return 'mid';
  if (value <= 60) return 'high';
  return 'extreme';
};

const getTierData = (type, score) => {
  if (type === 'chemistry') {
    if (score >= 85) return { icon: '🔥', t1: '서로에게 빠르게 끌립니다.', t2: '초반 친밀감 형성이 매우 쉽습니다.' };
    if (score >= 70) return { icon: '💞', t1: '호감이 잘 쌓이는 조합입니다.', t2: '첫 흐름이 부드럽습니다.' };
    if (score >= 55) return { icon: '🌿', t1: '천천히 가까워지는 타입입니다.', t2: '편안함이 먼저 형성됩니다.' };
    if (score >= 40) return { icon: '🤝', t1: '무난한 시작이 가능합니다.', t2: '자극보다 안정이 큰 조합입니다.' };
    return { icon: '🧊', t1: '초반 끌림은 약할 수 있습니다.', t2: '서로의 리듬을 맞추는 시간이 필요합니다.' };
  }

  if (type === 'friction') {
    if (score >= 80) return { icon: '🌊', t1: '갈등 빈도가 높을 수 있습니다.', t2: '감정/거리/기준 충돌이 큽니다.' };
    if (score >= 60) return { icon: '⚡', t1: '조율이 자주 필요합니다.', t2: '규칙을 미리 정하면 안정됩니다.' };
    if (score >= 40) return { icon: '🔄', t1: '조정 가능한 수준입니다.', t2: '대화 방식만 맞추면 충분히 풀립니다.' };
    return { icon: '🧱', t1: '큰 마찰은 낮은 편입니다.', t2: '갈등 관리 난이도가 낮습니다.' };
  }

  if (type === 'direction') {
    if (score >= 85) return { icon: '🧭', t1: '장기 방향이 잘 맞습니다.', t2: '중요한 선택에서 합의가 쉽습니다.' };
    if (score >= 70) return { icon: '🌱', t1: '큰 방향은 유사합니다.', t2: '세부 방식만 조율하면 됩니다.' };
    if (score >= 55) return { icon: '↔', t1: '핵심 지점에서 차이가 있습니다.', t2: '미래 대화를 일찍 시작하는 게 좋습니다.' };
    if (score >= 40) return { icon: '⚠️', t1: '중장기 계획 충돌 가능성이 있습니다.', t2: '우선순위 합의가 필요합니다.' };
    return { icon: '🧱', t1: '장기 방향 차이가 큽니다.', t2: '관계 운영 원칙부터 재설계가 필요합니다.' };
  }

  return { icon: '•', t1: '', t2: '' };
};

const getDisplayNames = (details, harmonyData) => {
  const myId = harmonyData.user_a_id === harmonyData.other_user_id
    ? harmonyData.user_b_id
    : harmonyData.user_a_id;

  const asLabel = (id) => (id === myId ? '나' : `@${id}`);

  return {
    a: asLabel(details.id_a),
    b: asLabel(details.id_b),
  };
};

const getDetailList = (type, details, harmonyData) => {
  if (!details || !harmonyData) return [];

  const { s_diff, e_diff, b_diff, b_match, sa, sb, ea, eb, ba, bb } = details;
  const names = getDisplayNames(details, harmonyData);

  const lines = [];

  if (type === 'chemistry') {
    if (s_diff <= 25) lines.push('감정 표현 템포가 비슷해 초반 대화가 편합니다.');
    else if (s_diff <= 40) lines.push('감정 표현 속도 차이가 있어 초반에는 탐색 시간이 필요합니다.');
    else lines.push(`감정 반응 속도가 다릅니다. ${sa < sb ? names.a : names.b}는 빠른 확인을, ${sa < sb ? names.b : names.a}는 정리 시간을 원할 수 있습니다.`);

    if (e_diff <= 25) lines.push('연락 빈도와 거리감 기대치가 유사합니다.');
    else if (e_diff <= 40) lines.push('한쪽은 자주 소통, 한쪽은 개인 시간을 더 필요로 할 수 있습니다.');
    else lines.push('연락 리듬 차이가 커서 서운함이 생기기 쉽습니다.');

    if (b_match) lines.push('좋아하는 관계의 방향이 비슷해 초반 공감대가 빠릅니다.');
  }

  if (type === 'friction') {
    if (s_diff > 55) lines.push('갈등 시 대화 타이밍이 크게 어긋날 수 있습니다.');
    else if (s_diff > 35) lines.push('한쪽은 바로 풀고 싶고, 다른 한쪽은 시간을 두고 싶을 수 있습니다.');
    else if ((sa + sb) / 2 >= 75) lines.push('둘 다 참는 편이라, 표면적 평화 뒤에 누적된 서운함이 생길 수 있습니다.');
    else if ((sa + sb) / 2 <= 35) lines.push('둘 다 즉각 반응하는 편이라 대화 온도가 빨리 올라갈 수 있습니다.');

    if (e_diff > 55) lines.push('위로를 기대하는 방식이 달라 오해가 커질 수 있습니다.');
    else if (e_diff > 35) lines.push('공감 우선 vs 해결 우선 차이로 엇갈릴 수 있습니다.');

    if (b_diff > 55) lines.push('의사결정 기준 차이가 커서 반복 충돌 가능성이 있습니다.');
    else if (b_diff > 35) lines.push('원칙 중심과 상황 중심 접근이 섞이며 마찰이 생길 수 있습니다.');

    if (lines.length === 0) lines.push('갈등 구조가 비교적 단순해 규칙만 정하면 안정적입니다.');
  }

  if (type === 'direction') {
    if (b_match && b_diff <= 25 && e_diff <= 25) {
      lines.push('장기 가치관과 일상 운영 방식이 모두 유사합니다.');
      lines.push('지속 가능한 장기 관계에 유리한 조합입니다.');
    } else if (b_match) {
      lines.push('큰 방향은 같지만, 실행 방식 조율이 필요합니다.');
      lines.push('역할 분담을 명확히 하면 장점이 커집니다.');
    } else {
      lines.push('중요한 가치 우선순위가 다를 수 있습니다.');
      lines.push('커리어/돈/시간 배분 기준을 초반에 합의하는 것이 중요합니다.');
    }
  }

  return lines.slice(0, 3);
};

const getMainAxisGuide = (details) => {
  if (!details) return null;

  const axisDiffs = [
    { axis: 'S', diff: details.s_diff || 0 },
    { axis: 'E', diff: details.e_diff || 0 },
    { axis: 'B', diff: details.b_diff || 0 },
  ].sort((a, b) => b.diff - a.diff);

  const top = axisDiffs[0];

  if (top.axis === 'S') {
    return {
      axis: 'S축',
      issue: '감정 타이밍 충돌',
      why: '한쪽은 바로 확인하고, 다른 한쪽은 정리 후 말하려는 패턴이 충돌합니다.',
      fix: [
        '갈등 직후 30초 상태 공유: "지금 생각 정리 중이야. 20분 뒤 이야기하자".',
        '대화 시작은 결론보다 감정 라벨 1개부터 말하기.',
        '당일 안에 재대화 시간 확정하기.'
      ],
      prevent: [
        '주 1회 10분 체크인: 서운함 누적 방지.',
        '잠수 금지 규칙: 답이 늦으면 복귀 시간 먼저 알리기.'
      ]
    };
  }

  if (top.axis === 'E') {
    return {
      axis: 'E축',
      issue: '연락/거리 기대치 충돌',
      why: '관계에서 필요한 연결 강도가 달라 서운함이 반복될 수 있습니다.',
      fix: [
        '평일/주말 연락 기준을 숫자로 합의하기.',
        '확인 질문은 비난 톤 대신 사실 확인 톤으로 말하기.',
        '혼자 시간 필요 신호 문장을 미리 정해두기.'
      ],
      prevent: [
        '바쁠 때 최소 신호 1개(예: "오늘 늦어, 밤 11시 연락") 남기기.',
        '주간 데이트/개인시간 비율을 고정하기.'
      ]
    };
  }

  return {
    axis: 'B축',
    issue: '의사결정 기준 충돌',
    why: '한쪽은 원칙 중심, 다른 한쪽은 상황 중심으로 판단해 같은 이슈가 반복됩니다.',
    fix: [
      '결정 전에 "이번 건 원칙 우선/상황 우선"을 먼저 선언하기.',
      '큰 결정은 즉시 결론 대신 24시간 숙려 규칙 적용.',
      '비난 대신 요청형 문장 사용: "다음엔 이렇게 해주면 좋아".'
    ],
    prevent: [
      '돈/시간/약속에 대한 기본 룰 3개 문서화.',
      '월 1회 관계 운영 점검: 잘된 점 1개, 고칠 점 1개 공유.'
    ]
  };
};

const getDeepNarrative = (details, scores) => {
  if (!details || !scores) {
    return {
      headline: '두 사람의 관계 운영 패턴을 불러오는 중입니다.',
      paragraphs: [],
      talkExamples: [],
    };
  }

  const sBand = getBand(details.s_diff || 0);
  const eBand = getBand(details.e_diff || 0);
  const bBand = getBand(details.b_diff || 0);
  const highest = [
    { axis: 'S', diff: details.s_diff || 0 },
    { axis: 'E', diff: details.e_diff || 0 },
    { axis: 'B', diff: details.b_diff || 0 },
  ].sort((a, b) => b.diff - a.diff)[0].axis;

  const rhythmText = sBand === 'low'
    ? '감정 반응 템포가 비슷해 갈등 후 회복 속도도 크게 어긋나지 않습니다.'
    : '감정 반응 템포 차이가 있어 갈등 직후 타이밍 충돌이 생기기 쉽습니다.';

  const distanceText = eBand === 'low'
    ? '연락 빈도와 거리감 기대치가 비슷해 일상 운영이 안정적입니다.'
    : '연락/거리 기대치 차이로 서운함의 해석이 엇갈릴 가능성이 있습니다.';

  const standardText = bBand === 'low'
    ? '결정 기준 차이가 작아서 장기 운영 규칙을 정하기 쉽습니다.'
    : '의사결정 기준 차이가 있어 같은 문제를 다른 기준으로 해석할 수 있습니다.';

  const focusAxisText = highest === 'S'
    ? '핵심 관리축은 S축(감정 타이밍)입니다.'
    : highest === 'E'
      ? '핵심 관리축은 E축(연락/거리 기대치)입니다.'
      : '핵심 관리축은 B축(의사결정 기준)입니다.';

  const talkExamples = highest === 'S'
    ? [
      '“지금은 감정이 올라와서 20분만 정리하고 다시 이야기할게.”',
      '“결론 전에 내 감정 한 줄부터 말해볼게. 나는 지금 불안해.”',
    ]
    : highest === 'E'
      ? [
        '“오늘은 바빠서 짧게만 연락할게. 밤 11시에 다시 이야기하자.”',
        '“지금 필요한 건 공감이야, 해결책은 10분 뒤에 같이 찾자.”',
      ]
      : [
        '“이번 건 원칙 우선으로 볼지, 상황 우선으로 볼지 먼저 정하자.”',
        '“지금 결론 내지 말고 내일 다시 결정하자.”',
      ];

  return {
    headline: `${scores.chemistry_score}% 끌림, ${scores.friction_score}% 갈등 리스크, ${scores.direction_score}% 장기 방향성의 조합입니다.`,
    paragraphs: [
      `초반에는 ${scores.chemistry_score >= 70 ? '호감 형성이 빠른 편' : '속도를 맞추며 가까워지는 편'}입니다. ${rhythmText}`,
      `관계가 진행되면 ${distanceText} ${standardText}`,
      `${focusAxisText} 이 축을 먼저 맞추면 갈등이 줄고 관계 안정성이 빠르게 올라갑니다.`,
    ],
    talkExamples,
  };
};


const EnglishCollaborativeReport = ({ data, navigate }) => {
  const scores = [
    { key: 'chemistry', title: 'Initial Attraction', value: data.chemistry_score || 0, text: 'How naturally the first interaction may gain momentum.' },
    { key: 'friction', title: 'Friction Risk', value: data.friction_score || 0, text: 'How often communication styles may require adjustment.' },
    { key: 'direction', title: 'Long-term Direction', value: data.direction_score || 0, text: 'How closely major relationship priorities align.' },
  ];
  const details = data.details || {};
  const differences = [
    { axis: 'S', label: 'Emotional timing', value: details.s_diff || 0 },
    { axis: 'E', label: 'Connection and distance', value: details.e_diff || 0 },
    { axis: 'B', label: 'Relationship values', value: details.b_diff || 0 },
  ];
  const main = [...differences].sort((a, b) => b.value - a.value)[0];

  return (
    <div className="res-container">
      <div className="res-card">
        <header className="res-header">
          <span className="badge">1:1 HARMONY REPORT</span>
          <h1>Harmony with @{data.other_user_id}</h1>
          <p className="header-sub">An interpretation based on scores and practical behavior patterns.</p>
        </header>
        <div className="summary-grid">
          {scores.map((item) => (
            <div key={item.key} className="summary-chip">
              <div className="chip-title">{item.title}</div>
              <div className="chip-value">{item.value}%</div>
              <div className="chip-text">{item.text}</div>
            </div>
          ))}
        </div>
        <div className="deep-box">
          <div className="deep-title">Main Adjustment Point</div>
          <div className="deep-headline">{main.axis} · {main.label}</div>
          <p>The largest difference is {main.value} points. Agree on a shared communication rule before tension rises.</p>
          <p>Use a clear pause-and-return time during conflict, confirm whether empathy or a solution is needed, and discuss expectations in concrete terms.</p>
        </div>
        <div className="action-card evidence">
          <div className="action-title">Evidence</div>
          {differences.map((item) => (
            <div className="evidence-row" key={item.axis}>
              <span>{item.axis} · {item.label}</span>
              <strong>{item.value} points</strong>
            </div>
          ))}
          <div className="evidence-row"><span>Ideal match</span><strong>{details.b_match ? 'Match' : 'Different'}</strong></div>
        </div>
        <div className="res-footer">
          <button className="back-btn" onClick={() => navigate('/harmony/lobby')}>Back to List</button>
        </div>
      </div>
    </div>
  );
};

const CollaborativeResult = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isEnglish = (i18n.resolvedLanguage || i18n.language).startsWith('en');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await getCollaborativeHarmonyDetail(id);
        setData(res);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id]);

  const quickSummary = useMemo(() => {
    if (!data) return null;

    const chemistry = getTierData('chemistry', data.chemistry_score || 0);
    const friction = getTierData('friction', data.friction_score || 0);
    const direction = getTierData('direction', data.direction_score || 0);

    return [
      { key: 'chemistry', title: '초반 끌림', value: data.chemistry_score, text: chemistry.t1 },
      { key: 'friction', title: '갈등 리스크', value: data.friction_score, text: friction.t1 },
      { key: 'direction', title: '장기 방향', value: data.direction_score, text: direction.t1 },
    ];
  }, [data]);

  if (loading) {
    return (
      <div className="loading" style={{ color: 'white', padding: '2rem', textAlign: 'center' }}>
        분석 중...
      </div>
    );
  }

  if (error) {
    return (
      <div className="error" style={{ color: 'red', padding: '2rem', textAlign: 'center' }}>
        {error}
      </div>
    );
  }

  if (isEnglish) return <EnglishCollaborativeReport data={data} navigate={navigate} />;

  const guide = getMainAxisGuide(data?.details);
  const deepNarrative = getDeepNarrative(data?.details, {
    chemistry_score: data?.chemistry_score || 0,
    friction_score: data?.friction_score || 0,
    direction_score: data?.direction_score || 0,
  });

  const renderBlock = (type, titleStr, score, details) => {
    const tier = getTierData(type, score || 0);
    const detailList = getDetailList(type, details, data);

    return (
      <div className="score-block">
        <div className="block-header">
          <span className="block-title">{titleStr}</span>
          <span className="block-icon">{tier.icon}</span>
        </div>

        <div className="bar-container">
          <motion.div
            className={`bar ${type}`}
            initial={{ width: 0 }}
            animate={{ width: `${score}%` }}
            transition={{ duration: 0.8, delay: 0.1 }}
          />
        </div>

        <div className="block-footer">
          <div className="label-container">
            <span className="label-text">{tier.t1}</span>
            {tier.t2 && <span className="label-subtext">{tier.t2}</span>}
          </div>
          <span className="score-val">{score}%</span>
        </div>

        {detailList.length > 0 && (
          <div className="detail-box">
            {detailList.map((txt, idx) => (
              <div key={idx} className="detail-item">
                <span className="detail-dot">•</span>
                <span className="detail-text">{txt}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="res-container">
      <div className="res-card">
        <header className="res-header">
          <span className="badge">1:1 궁합 리포트</span>
          <h1>@{data.other_user_id} 님과의 궁합</h1>
          <p className="header-sub">점수 + 실제 행동 기준으로 해석한 결과입니다.</p>
        </header>

        <div className="summary-grid">
          {quickSummary?.map((item) => (
            <div key={item.key} className="summary-chip">
              <div className="chip-title">{item.title}</div>
              <div className="chip-value">{item.value}%</div>
              <div className="chip-text">{item.text}</div>
            </div>
          ))}
        </div>

        <div className="tab-row">
          <button
            className={activeTab === 'summary' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('summary')}
          >
            결과 요약
          </button>
          <button
            className={activeTab === 'action' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('action')}
          >
            행동 가이드
          </button>
        </div>

        {activeTab === 'summary' ? (
          <>
            <div className="scores-section">
              {renderBlock('chemistry', '초반 끌림', data.chemistry_score, data.details)}
              {renderBlock('friction', '갈등 리스크', data.friction_score, data.details)}
              {renderBlock('direction', '장기 방향', data.direction_score, data.details)}
            </div>
            <div className="deep-box">
              <div className="deep-title">심층 해설</div>
              <div className="deep-headline">{deepNarrative.headline}</div>
              {deepNarrative.paragraphs.map((p, idx) => (
                <p key={idx}>{p}</p>
              ))}
            </div>
          </>
        ) : (
          <div className="action-section">
            <div className="action-card issue">
              <div className="action-title">예상 문제</div>
              <h3>{guide?.axis} · {guide?.issue}</h3>
              <p>{guide?.why}</p>
            </div>

            <div className="action-card fix">
              <div className="action-title">해결 방법</div>
              {guide?.fix.map((line, idx) => (
                <div key={idx} className="line-item">• {line}</div>
              ))}
            </div>

            <div className="action-card prevent">
              <div className="action-title">예방 방법</div>
              {guide?.prevent.map((line, idx) => (
                <div key={idx} className="line-item">• {line}</div>
              ))}
            </div>

            <div className="action-card evidence">
              <div className="action-title">근거 데이터</div>
              <div className="evidence-row">
                <span>S축 차이</span>
                <strong>{data?.details?.s_diff ?? 0}점 ({BAND_TEXT[getBand(data?.details?.s_diff ?? 0)]})</strong>
              </div>
              <div className="evidence-row">
                <span>E축 차이</span>
                <strong>{data?.details?.e_diff ?? 0}점 ({BAND_TEXT[getBand(data?.details?.e_diff ?? 0)]})</strong>
              </div>
              <div className="evidence-row">
                <span>B축 차이</span>
                <strong>{data?.details?.b_diff ?? 0}점 ({BAND_TEXT[getBand(data?.details?.b_diff ?? 0)]})</strong>
              </div>
              <div className="evidence-row">
                <span>이상향 일치</span>
                <strong>{data?.details?.b_match ? '일치' : '불일치'}</strong>
              </div>
            </div>
            <div className="action-card dialog">
              <div className="action-title">추천 대화 문장</div>
              {deepNarrative.talkExamples.map((line, idx) => (
                <div key={idx} className="line-item">• {line}</div>
              ))}
            </div>
          </div>
        )}

        <div className="res-footer">
          <button className="back-btn" onClick={() => navigate('/harmony/lobby')}>
            목록으로
          </button>
          <button className="share-btn" onClick={() => alert('공유 기능은 준비 중입니다.')}>
            궁합 공유하기
          </button>
        </div>
      </div>

      <style jsx>{`
        .res-container {
          min-height: 100vh;
          background: linear-gradient(180deg, #0f172a 0%, #111827 100%);
          padding: 7rem 1rem 3rem;
          display: flex;
          justify-content: center;
          color: white;
        }
        .res-card {
          max-width: 760px;
          width: 100%;
        }
        .res-header {
          text-align: left;
          margin-bottom: 1.25rem;
        }
        .badge {
          background: rgba(59, 130, 246, 0.18);
          color: #93c5fd;
          border: 1px solid rgba(147, 197, 253, 0.35);
          padding: 0.35rem 0.75rem;
          border-radius: 999px;
          font-size: 0.76rem;
          font-weight: 700;
        }
        .res-header h1 {
          margin: 0.75rem 0 0.35rem;
          font-size: 1.55rem;
          font-weight: 800;
          color: #f8fafc;
        }
        .header-sub {
          margin: 0;
          font-size: 0.92rem;
          color: #94a3b8;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0.75rem;
          margin-bottom: 1rem;
        }
        .summary-chip {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 14px;
          padding: 0.8rem;
        }
        .chip-title {
          font-size: 0.8rem;
          color: #93c5fd;
          margin-bottom: 0.25rem;
        }
        .chip-value {
          font-size: 1.25rem;
          font-weight: 800;
          color: #f8fafc;
          margin-bottom: 0.2rem;
        }
        .chip-text {
          font-size: 0.82rem;
          color: #cbd5e1;
          line-height: 1.45;
        }

        .tab-row {
          display: flex;
          gap: 0.5rem;
          margin: 1rem 0;
        }
        .tab {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #cbd5e1;
          border-radius: 10px;
          padding: 0.65rem 0.95rem;
          font-size: 0.9rem;
          font-weight: 700;
          cursor: pointer;
        }
        .tab.active {
          background: rgba(59, 130, 246, 0.2);
          border-color: rgba(147, 197, 253, 0.45);
          color: #dbeafe;
        }

        .scores-section {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
          margin-bottom: 1.2rem;
        }
        .score-block {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 16px;
          padding: 1rem;
        }
        .block-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.65rem;
        }
        .block-title {
          font-size: 1rem;
          font-weight: 800;
          color: #f8fafc;
        }
        .block-icon {
          font-size: 1.3rem;
        }

        .bar-container {
          height: 10px;
          background: rgba(255, 255, 255, 0.08);
          border-radius: 999px;
          overflow: hidden;
          margin-bottom: 0.65rem;
        }
        .bar {
          height: 100%;
          border-radius: 999px;
        }
        .bar.chemistry {
          background: linear-gradient(to right, #fb7185, #f43f5e);
        }
        .bar.friction {
          background: linear-gradient(to right, #60a5fa, #2563eb);
        }
        .bar.direction {
          background: linear-gradient(to right, #34d399, #10b981);
        }

        .block-footer {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 0.75rem;
          margin-bottom: 0.65rem;
        }
        .label-container {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
          flex: 1;
        }
        .label-text {
          font-size: 0.94rem;
          font-weight: 700;
          color: #f8fafc;
          line-height: 1.35;
        }
        .label-subtext {
          font-size: 0.82rem;
          color: #94a3b8;
          line-height: 1.35;
        }
        .score-val {
          font-size: 1.1rem;
          font-weight: 800;
          color: #e2e8f0;
          white-space: nowrap;
        }

        .detail-box {
          background: rgba(2, 6, 23, 0.6);
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 12px;
          padding: 0.7rem;
          display: flex;
          flex-direction: column;
          gap: 0.45rem;
        }
        .detail-item {
          display: flex;
          gap: 0.35rem;
          align-items: flex-start;
        }
        .detail-dot {
          color: #93c5fd;
          font-weight: 800;
        }
        .detail-text {
          font-size: 0.84rem;
          color: #cbd5e1;
          line-height: 1.45;
        }

        .action-section {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 0.75rem;
          margin-bottom: 1.2rem;
        }
        .deep-box {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(148, 163, 184, 0.3);
          border-radius: 14px;
          padding: 0.95rem 1rem;
          margin-bottom: 1.2rem;
        }
        .deep-title {
          font-size: 0.78rem;
          text-transform: uppercase;
          color: #93c5fd;
          letter-spacing: 0.05em;
          margin-bottom: 0.3rem;
        }
        .deep-headline {
          font-size: 0.93rem;
          font-weight: 700;
          color: #f8fafc;
          margin-bottom: 0.45rem;
          line-height: 1.4;
        }
        .deep-box p {
          margin: 0.28rem 0;
          font-size: 0.86rem;
          color: #cbd5e1;
          line-height: 1.5;
        }
        .action-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 14px;
          padding: 0.9rem;
        }
        .action-card.issue {
          border-color: rgba(251, 113, 133, 0.35);
          background: rgba(251, 113, 133, 0.08);
        }
        .action-card.fix {
          border-color: rgba(96, 165, 250, 0.35);
          background: rgba(96, 165, 250, 0.08);
        }
        .action-card.prevent {
          border-color: rgba(52, 211, 153, 0.35);
          background: rgba(52, 211, 153, 0.08);
        }
        .action-card.dialog {
          border-color: rgba(167, 139, 250, 0.35);
          background: rgba(167, 139, 250, 0.1);
        }
        .action-title {
          font-size: 0.78rem;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #cbd5e1;
          margin-bottom: 0.35rem;
        }
        .action-card h3 {
          margin: 0 0 0.35rem;
          font-size: 1rem;
          color: #f8fafc;
        }
        .action-card p {
          margin: 0;
          font-size: 0.88rem;
          line-height: 1.45;
          color: #e2e8f0;
        }
        .line-item {
          font-size: 0.86rem;
          color: #e2e8f0;
          line-height: 1.45;
          margin-top: 0.25rem;
        }
        .evidence-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.6rem;
          font-size: 0.85rem;
          color: #cbd5e1;
          margin-top: 0.4rem;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
          padding-top: 0.4rem;
        }
        .evidence-row strong {
          color: #f8fafc;
          font-size: 0.83rem;
        }

        .res-footer {
          display: flex;
          gap: 0.7rem;
        }
        .back-btn,
        .share-btn {
          flex: 1;
          padding: 0.95rem;
          border-radius: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: 0.2s;
        }
        .back-btn {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.15);
          color: #e2e8f0;
        }
        .share-btn {
          background: #2563eb;
          border: 1px solid rgba(147, 197, 253, 0.35);
          color: white;
        }

        @media (max-width: 760px) {
          .summary-grid,
          .action-section {
            grid-template-columns: 1fr;
          }
          .res-container {
            padding-top: 5.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default CollaborativeResult;
